import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';
import TaxIntel from '../components/shared/TaxIntel';
import Text from '../components/shared/Text';
import QuickRegistration from '../components/vendor/QuickRegistration';

// Minimalist Procurement Form matching ProductsIndex
function PurchaseForm() {
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        vendor_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        invoice_number: '',
        notes: '',
        location_id: '',
        status: 'received',
        landed_cost: 0,
        paid_amount: 0,
        payment_method: 'cash'
    });

    const [locations, setLocations] = useState([]);
    const [vendor, setVendor] = useState<any>(null);
    const [vendorSearch, setVendorSearch] = useState('');
    const [vendorResults, setVendorResults] = useState([]);
    const [showVendorResults, setShowVendorResults] = useState(false);
    const [showNewVendorForm, setShowNewVendorForm] = useState(false);

    const [lineItems, setLineItems] = useState<any[]>([]);
    const [productSearch, setProductSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [showProductResults, setShowProductResults] = useState(false);

    const [taxRates, setTaxRates] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchLocations();
        fetchTaxRates();

        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowProductResults(false);
                setShowVendorResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await api.get('/locations');
            const fetchedLocations = response.data?.data || [];
            setLocations(fetchedLocations);
            if (fetchedLocations.length > 0) {
                const primary = fetchedLocations.find((loc: any) => loc.is_primary);
                setFormData(prev => ({ ...prev, location_id: primary ? primary.id : fetchedLocations[0].id }));
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const searchVendors = async (query: string) => {
        if (query.length < 2) {
            setVendorResults([]);
            setShowVendorResults(false);
            return;
        }

        try {
            const response = await api.get(`/search/vendors?q=${encodeURIComponent(query)}`);
            let vendors = [];
            if (response.data && Array.isArray(response.data.vendors)) vendors = response.data.vendors;
            else if (Array.isArray(response.vendors)) vendors = response.vendors;
            else if (Array.isArray(response.data)) vendors = response.data;
            
            setVendorResults(vendors);
            setShowVendorResults(true);
        } catch (error) {
            console.error('Error searching vendors:', error);
        }
    };

    const selectVendor = (v: any) => {
        setVendor(v);
        setVendorSearch(v.name);
        setFormData(prev => ({ ...prev, vendor_id: v.id }));
        setShowVendorResults(false);
    };

    const searchProducts = async (query: string) => {
        if (!query || query.length < 2) {
            setProductResults([]);
            setShowProductResults(false);
            return;
        }

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await api.get(`/search/products?q=${encodeURIComponent(query)}&include_inactive=true&limit=20`);
                let products = [];
                if (response.data && Array.isArray(response.data.products)) products = response.data.products;
                else if (Array.isArray(response.vendors)) products = response.vendors;
                else if (Array.isArray(response.data)) products = response.data;
                
                setProductResults(products);
                setShowProductResults(true); // Always show — will render empty state
            } catch (error) {
                console.error('Error searching products:', error);
            }
        }, 300);
    };

    const addLineItem = (product: any) => {
        setLineItems(prev => [...prev, {
            product_id: product.id,
            product_name: product.name,
            quantity: 1,
            unit_price: product.purchase_price || product.mrp || 0,
            mrp: product.mrp || product.selling_price || 0,
            unit: product.unit || 'piece',
            sku: product.sku,
            tax_rate_id: product.tax_rate_id || '',
            tax_rate: parseFloat(product.tax_rate || 0),
            tax_inclusive: false,
            tax_type: 'auto',
            cgst_rate: 0,
            sgst_rate: 0,
            igst_rate: 0,
        }]);
        setProductSearch('');
        setShowProductResults(false);
    };

    const removeLineItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateLineItem = (index: number, field: string, value: any) => {
        setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (lineItems.length === 0) {
            toast.error('Add at least one product.');
            return;
        }

        try {
            setSaving(true);

            const { paid_amount, payment_method, ...restFormData } = formData;
            const purchasePayload = {
                ...restFormData,
                vendor_invoice_number: restFormData.invoice_number,
                // Landed cost removed as requested
                landed_cost: 0,
                purchase_items_attributes: lineItems.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    mrp: item.mrp,
                    unit: item.unit,
                    manual_tax_rate: item.tax_rate,
                    tax_rate_id: item.tax_rate_id || null,
                    tax_type: item.tax_type,
                    cgst_rate: item.cgst_rate,
                    sgst_rate: item.sgst_rate,
                    igst_rate: item.igst_rate
                }))
            } as any;

            if (formData.paid_amount > 0) {
                purchasePayload.payments_attributes = [{
                    amount: formData.paid_amount,
                    payment_method: formData.payment_method || 'cash'
                }];
            }

            const response = await api.post('/purchases', { purchase: purchasePayload });
            
            if (response.success) {
                toast.success('Purchase recorded successfully — stock updated!');
                navigate('/purchases');
            } else {
                const backendErrors = response.errors?.length > 0 ? response.errors : [response.message || 'Failed to save purchase'];
                toast.error(backendErrors[0]);
            }
        } catch (err: any) {
            toast.error(err.message || 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let taxAmount = 0;
        let totalCgst = 0;
        let totalSgst = 0;
        let totalIgst = 0;

        const currentFirm = (window as any).CURRENT_FIRM || {};
        const firmState = currentFirm.state || currentFirm.state_code || '';
        const partnerState = vendor?.state || vendor?.state_code || '';
        
        // Improved inter-state detection
        const isInterState = firmState && partnerState && 
                            firmState.trim().toLowerCase() !== partnerState.trim().toLowerCase();

        lineItems.forEach(item => {
            const qty = Number(item.quantity) || 0;
            const price = Number(item.unit_price) || 0;
            const itemSubtotal = qty * price;
            
            let itemTax = 0;
            if (item.tax_type === 'cgst_sgst') {
                const cgst = itemSubtotal * ((item.cgst_rate || 0) / 100);
                const sgst = itemSubtotal * ((item.sgst_rate || 0) / 100);
                itemTax = cgst + sgst;
                totalCgst += cgst;
                totalSgst += sgst;
            } else if (item.tax_type === 'igst') {
                const igst = itemSubtotal * ((item.igst_rate || 0) / 100);
                itemTax = igst;
                totalIgst += igst;
            } else {
                itemTax = itemSubtotal * ((Number(item.tax_rate) || 0) / 100);
                if (isInterState) {
                    totalIgst += itemTax;
                } else {
                    totalCgst += itemTax / 2;
                    totalSgst += itemTax / 2;
                }
            }

            subtotal += itemSubtotal;
            taxAmount += itemTax;
        });

        // Landed cost removed from calculation
        const total = subtotal + taxAmount - (Number(formData.discount_amount) || 0);
        return { subtotal, totalCgst, totalSgst, totalIgst, taxAmount, total, isInterState };
    };

    const { subtotal, taxAmount, total, isInterState, totalIgst } = calculateTotals();

    return (
        <div className="mt-6 px-1 max-w-5xl mx-auto" ref={wrapperRef}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <Text variant="h2" color="primary">New Purchase</Text>
                    <Text variant="small" color="slate" className="mt-0.5">
                        Record a new procurement entry
                    </Text>
                </div>
                <Link
                    to="/purchases"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-sm"
                >
                    Cancel
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 pb-24 sm:pb-0">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Vendor</label>
                        {!vendor ? (
                            <>
                                <input
                                    type="text"
                                    placeholder="Search vendors..."
                                    value={vendorSearch}
                                    onChange={(e) => {
                                        setVendorSearch(e.target.value);
                                        searchVendors(e.target.value);
                                        if (!e.target.value) {
                                            setVendor(null);
                                            setShowNewVendorForm(false);
                                        }
                                    }}
                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                                />
                                {showVendorResults && vendorSearch.length >= 2 && !showNewVendorForm && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                        {vendorResults.length > 0 ? (
                                            vendorResults.map((v: any) => (
                                                <div
                                                    key={v.id}
                                                    onClick={() => selectVendor(v)}
                                                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer text-sm border-b last:border-0 border-slate-100"
                                                >
                                                    <div className="font-medium text-slate-900">{v.name}</div>
                                                    <div className="text-xs text-slate-500">{v.phone || v.email}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-4 text-center">
                                                <p className="text-slate-500 text-sm font-medium mb-3">Vendor not found</p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowVendorResults(false);
                                                        setShowNewVendorForm(true);
                                                    }}
                                                    className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
                                                >
                                                    + Register "{vendorSearch}"
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex items-center justify-between p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl">
                                <div>
                                    <p className="font-semibold text-slate-900 text-sm">{vendor.name}</p>
                                    <p className="text-xs text-slate-500">{vendor.phone || vendor.email}</p>
                                    <p className="text-[10px] text-indigo-600 font-bold uppercase">{vendor.state || 'No State'}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setVendor(null);
                                        setVendorSearch('');
                                        setFormData(prev => ({ ...prev, vendor_id: '' }));
                                    }}
                                    className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                            </div>
                        )}

                        {showNewVendorForm && (
                            <div className="mt-3 animate-in slide-in-from-top-2 duration-300">
                                <QuickRegistration
                                    initialName={vendorSearch}
                                    onSuccess={(v) => {
                                        selectVendor(v);
                                        setShowNewVendorForm(false);
                                    }}
                                    onCancel={() => setShowNewVendorForm(false)}
                                />
                            </div>
                        )}
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Date</label>
                        <input
                            type="date"
                            name="purchase_date"
                            value={formData.purchase_date}
                            onChange={(e) => setFormData(prev => ({...prev, purchase_date: e.target.value}))}
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Invoice Reference</label>
                        <input
                            type="text"
                            name="invoice_number"
                            value={formData.invoice_number}
                            onChange={(e) => setFormData(prev => ({...prev, invoice_number: e.target.value}))}
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                            placeholder="INV-..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Location</label>
                        <select
                            name="location_id"
                            value={formData.location_id}
                            onChange={(e) => setFormData(prev => ({...prev, location_id: e.target.value}))}
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                        >
                            {locations.map((loc: any) => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                    <div className="flex justify-between items-end mb-4">
                        <Text variant="h3" color="primary">Products</Text>
                        <div className="relative w-64">
                            <input
                                type="text"
                                placeholder="Search to add product..."
                                value={productSearch}
                                onChange={(e) => {
                                    setProductSearch(e.target.value);
                                    searchProducts(e.target.value);
                                }}
                                onFocus={() => { if (productSearch.length > 1) setShowProductResults(true); }}
                                className="w-full pl-8 pr-4 py-2 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                            />
                            <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            
                            {showProductResults && (
                                <div className="absolute z-50 w-72 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                                    {productResults.length > 0 ? productResults.map((p: any) => (
                                        <div
                                            key={p.id}
                                            onMouseDown={(e) => { e.preventDefault(); addLineItem(p); }}
                                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-sm border-b last:border-0 border-slate-100"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <div className="font-medium text-slate-900">{p.name}</div>
                                                    <div className="text-xs text-slate-500">{p.sku ? `SKU: ${p.sku}` : 'No SKU'} &bull; Stock: {p.stock_quantity ?? '?'}</div>
                                                </div>
                                                <div className="text-right flex flex-col items-end ml-2 shrink-0">
                                                    <div className="font-bold text-indigo-600 text-[10px]">Buy: ₹{parseFloat((p.purchase_price as any) || 0).toFixed(2)}</div>
                                                    <div className="font-medium text-slate-400 text-[10px] mt-0.5">MRP: ₹{parseFloat((p.selling_price as any) || 0).toFixed(2)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="p-4 text-center text-slate-500 text-sm">
                                            No products found for "{productSearch}".
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {lineItems.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 border-y border-slate-200 text-slate-600">
                                    <tr>
                                        <th className="py-3 px-4 font-medium">Product</th>
                                        <th className="py-3 px-4 font-medium w-24">Qty</th>
                                        <th className="py-3 px-4 font-medium w-32">Buy Price</th>
                                        <th className="py-3 px-4 font-medium w-32">MRP</th>
                                        <th className="py-3 px-4 font-medium w-40">Tax</th>
                                        <th className="py-3 px-4 font-medium text-right">Total</th>
                                        <th className="py-3 px-4 w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {lineItems.map((item, index) => {
                                        const qty = Number(item.quantity) || 0;
                                        const price = Number(item.unit_price) || 0;
                                        const sub = qty * price;
                                        let itemTax = 0;
                                        if (item.tax_type === 'cgst_sgst') {
                                            itemTax = sub * (((item.cgst_rate || 0) + (item.sgst_rate || 0)) / 100);
                                        } else if (item.tax_type === 'igst') {
                                            itemTax = sub * ((item.igst_rate || 0) / 100);
                                        } else {
                                            itemTax = sub * ((Number(item.tax_rate) || 0) / 100);
                                        }
                                        return (
                                            <tr key={index}>
                                                <td className="py-3 px-4">
                                                    <div className="font-medium text-slate-900">{item.product_name}</div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <input
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:border-indigo-500 outline-none"
                                                        min="1"
                                                    />
                                                </td>
                                                <td className="py-3 px-4">
                                                    <input
                                                        type="number"
                                                        value={item.unit_price}
                                                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:border-indigo-500 outline-none"
                                                    />
                                                </td>
                                                <td className="py-3 px-4">
                                                    <input
                                                        type="number"
                                                        value={item.mrp}
                                                        onChange={(e) => updateLineItem(index, 'mrp', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:border-emerald-500 outline-none text-emerald-700 font-medium"
                                                    />
                                                </td>
                                                <td className="py-3 px-4">
                                                    <select
                                                        value={item.tax_rate_id || ''}
                                                        onChange={(e) => {
                                                            const rateId = e.target.value;
                                                            const rate = taxRates.find(r => String(r.id) === String(rateId));
                                                            updateLineItem(index, 'tax_rate_id', rateId);
                                                            if (rate) updateLineItem(index, 'tax_rate', parseFloat(rate.percentage));
                                                        }}
                                                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:border-indigo-500 outline-none"
                                                    >
                                                        <option value="">No Tax</option>
                                                        {taxRates.map(tr => (
                                                            <option key={tr.id} value={tr.id}>{tr.name}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-3 px-4 text-right font-medium">
                                                    ₹{(sub + itemTax).toFixed(2)}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <button type="button" onClick={() => removeLineItem(index)} className="text-rose-500 hover:text-rose-700">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-12 text-center text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded-xl">
                            Search and add products to start creating a purchase record.
                        </div>
                    )}
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                            placeholder="Optional notes..."
                        />
                    </div>
                    
                    <div className="md:w-80 bg-slate-50 rounded-2xl border border-slate-200 p-6 space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Subtotal</span>
                            <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600">{isInterState ? 'IGST' : 'CGST/SGST'} (Tax)</span>
                            <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
                        </div>
                        
                        <div className="pt-3 border-t border-slate-200 flex justify-between">
                            <span className="font-semibold text-slate-900">Total (with Tax)</span>
                            <span className="font-bold text-lg text-indigo-600">₹{total.toFixed(2)}</span>
                        </div>
                        
                        <div className="pt-3 border-t border-slate-200 space-y-3">
                            <div className="flex justify-between items-center text-sm">
                                <span className="font-medium text-slate-700">Amount Paid</span>
                                <input
                                    type="number"
                                    value={formData.paid_amount || ''}
                                    onChange={(e) => setFormData(prev => ({...prev, paid_amount: parseFloat(e.target.value) || 0}))}
                                    className="w-28 px-3 py-1.5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg text-right font-bold focus:ring-2 focus:ring-emerald-500/20 outline-none"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-600">Payment Mode</span>
                                <select
                                    value={formData.payment_method}
                                    onChange={(e) => setFormData(prev => ({...prev, payment_method: e.target.value}))}
                                    className="w-28 px-2 py-1.5 border border-slate-200 rounded-lg text-right focus:border-indigo-500 outline-none"
                                >
                                    <option value="cash">Cash</option>
                                    <option value="bank_transfer">Bank/UPI</option>
                                    <option value="card">Card</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Save Bar on mobile, normal on desktop */}
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 px-4 py-3 sm:static sm:bg-transparent sm:border-0 sm:px-0 sm:py-0 sm:pt-4 sm:flex sm:justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full sm:w-auto px-8 py-3.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 active:scale-95 transition-all shadow-lg shadow-indigo-200 disabled:opacity-70 text-sm"
                    >
                        {saving ? 'Saving...' : '💾 Save Purchase'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default PurchaseForm;
